import { useEffect, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { countyService, CountyData, CropData } from '../services/countyService';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { webScrapingService } from '../services/webScrapingService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type SearchMode = 'county' | 'crop';
type ReportKind = 'county' | 'crop';

const CountyInfo: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (() => {
    const r = (user?.role ?? '').trim().toLowerCase();
    return r === 'admin' || r === 'super_admin' || r === 'superadmin';
  })();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounty, setSelectedCounty] = useState<CountyData | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<CropData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>('county');
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportKind, setReportKind] = useState<ReportKind>('county');
  const [kamisLoading, setKamisLoading] = useState(false);
  const [kamisError, setKamisError] = useState<string | null>(null);
  const [kamisRows, setKamisRows] = useState<any[]>([]);
  const [kamisIngesting, setKamisIngesting] = useState(false);
  const [kamisIngestMessage, setKamisIngestMessage] = useState<string | null>(null);

  const loadKamis = async (countyName: string) => {
    try {
      setKamisError(null);
      setKamisLoading(true);
      const resp = await webScrapingService.getKamisMarketPricesByCounty(countyName, 14, 60);
      setKamisRows(Array.isArray((resp as any)?.rows) ? (resp as any).rows : []);
    } catch (e) {
      setKamisRows([]);
      setKamisError(e instanceof Error ? e.message : 'Failed to load KAMIS market prices');
    } finally {
      setKamisLoading(false);
    }
  };

  const handleUploadKamis = async (files: FileList | File[]) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      setKamisIngestMessage(null);
      setKamisError('Admin access required');
      return;
    }
    if (!selectedCounty?.name) return;

    try {
      setKamisIngestMessage(null);
      setKamisError(null);
      setKamisIngesting(true);
      const list = Array.from(files as any).filter(Boolean) as File[];
      const parsedAll: any[] = [];
      for (const f of list) {
        const parsed = await webScrapingService.parseKamisExportFile(f);
        parsedAll.push(...parsed);
      }
      const result = await webScrapingService.ingestKamisMarketRows(parsedAll);
      setKamisIngestMessage(
        `Uploaded ${list.length} file(s). Parsed ${parsedAll.length} rows. Stored: ${(result as any)?.inserted ?? parsedAll.length}`,
      );
      await loadKamis(selectedCounty.name);
    } catch (e) {
      setKamisIngestMessage(null);
      setKamisError(e instanceof Error ? e.message : 'Failed to upload file');
    } finally {
      setKamisIngesting(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (searchMode === 'county') {
        const data = await countyService.getCountyByName(searchQuery);
        setSelectedCounty(data);
        setSelectedCrop(null);
      } else {
        const data = await countyService.getCropData(searchQuery);
        setSelectedCrop(data);
        setSelectedCounty(null);
      }
    } catch (err) {
      setError(`Failed to fetch ${searchMode} data. Please try again.`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCounty?.name) {
      setKamisRows([]);
      setKamisError(null);
      return;
    }
    loadKamis(selectedCounty.name);
  }, [selectedCounty?.name]);

  const handleRefresh = async () => {
    if (!searchQuery.trim()) return;
    await handleSearch();
  };

  const toCsvCell = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadCsv = (filename: string, rows: Array<Array<unknown>>) => {
    const content = rows.map((r) => r.map(toCsvCell).join(',')).join('\r\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printHtml = async (title: string, htmlBody: string) => {
    const logoUrl = `${window.location.origin}/icon.png`;
    const watermarkText = 'FarmersHub';

    const doc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>${title}</title>
    <style>
      @page { size: A4; margin: 18mm 14mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #111; }
      body::before {
        content: "${watermarkText}";
        position: fixed;
        top: 40%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-28deg);
        font-size: 92px;
        font-weight: 800;
        color: rgba(0,0,0,0.06);
        z-index: 0;
        pointer-events: none;
        white-space: nowrap;
      }
      .page { position: relative; z-index: 1; }
      .header { display:flex; align-items:center; justify-content:space-between; gap: 12px; margin-bottom: 14px; }
      .brand { display:flex; align-items:center; gap: 10px; }
      .brand img { width: 28px; height: 28px; border-radius: 6px; }
      .brand-name { font-weight: 800; font-size: 16px; letter-spacing: 0.2px; }
      .muted { color: #555; font-size: 12px; }
      h1 { font-size: 22px; margin: 10px 0 6px; }
      h2 { font-size: 14px; margin: 14px 0 8px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
      .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 12px; }
      .card .k { font-size: 12px; color: #555; }
      .card .v { font-size: 16px; font-weight: 800; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; }
      thead { display: table-header-group; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; font-size: 12px; vertical-align: top; }
      th { background: #f9fafb; text-align: left; }
      ul { padding-left: 18px; margin: 6px 0; }
      .row { display:flex; gap: 14px; }
      .col { flex: 1; min-width: 0; }
    </style>
  </head>
  <body>
    <div class="page">
      <div class="header">
        <div class="brand">
          <img src="${logoUrl}" alt="FarmersHub" />
          <div class="brand-name">FarmersHub</div>
        </div>
        <div class="muted">${new Date().toLocaleString()}</div>
      </div>
      ${htmlBody}
    </div>
  </body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) {
      iframe.remove();
      return;
    }

    iframeDoc.open();
    iframeDoc.write(doc);
    iframeDoc.close();

    await new Promise<void>((resolve) => {
      const done = () => resolve();
      iframe.onload = done;
      setTimeout(done, 250);
    });

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => iframe.remove(), 1000);
  };

  const openDetailedReport = () => {
    const hasData = Boolean(selectedCounty || selectedCrop);
    if (!hasData) return;
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    setReportKind(selectedCounty ? 'county' : 'crop');
    setReportOpen(true);
  };

  const exportReportCsv = () => {
    const nowIso = new Date().toISOString();
    if (reportKind === 'county' && selectedCounty) {
      const rows: Array<Array<unknown>> = [
        ['FarmersHub', 'County Detailed Report'],
        ['Generated at', nowIso],
        ['County', selectedCounty.name],
        ['Region', selectedCounty.region],
        [],
        ['Climate'],
        ['Temperature', selectedCounty.climate.temperature],
        ['Rainfall', selectedCounty.climate.rainfall],
        ['Altitude', selectedCounty.climate.altitude],
        [],
        ['Main crops'],
        ...selectedCounty.mainCrops.map((c) => [c]),
        [],
        ['Market prices'],
        ['Commodity', 'Price (KES)', 'Unit', 'Date'],
        ...selectedCounty.marketPrices.map((p) => [p.commodity, p.price, p.unit, new Date(p.date).toISOString().slice(0, 10)]),
        [],
        ['Government requirements - Licenses'],
        ...selectedCounty.governmentRequirements.licenses.map((l) => [l]),
        [],
        ['Government requirements - Permits'],
        ...selectedCounty.governmentRequirements.permits.map((p) => [p]),
        [],
        ['Government requirements - Regulations'],
        ...selectedCounty.governmentRequirements.regulations.map((r) => [r]),
        [],
        ['Farming tips'],
        ...selectedCounty.farmingTips.flatMap((cat) => [
          [cat.category],
          ...cat.tips.map((t) => ['', t]),
          [],
        ]),
        ['Resources'],
        ['Title', 'URL', 'Description'],
        ...selectedCounty.resources.map((r) => [r.title, r.url, r.description]),
      ];
      downloadCsv(`farmershub-county-report-${selectedCounty.name}-${nowIso.slice(0, 10)}.csv`, rows);
      return;
    }

    if (reportKind === 'crop' && selectedCrop) {
      const countyEntries = Object.entries(selectedCrop.countyData).sort((a, b) => b[1] - a[1]);
      const intlEntries = Object.entries(selectedCrop.internationalComparison).sort((a, b) => b[1] - a[1]);
      const rows: Array<Array<unknown>> = [
        ['FarmersHub', 'Crop Detailed Report'],
        ['Generated at', nowIso],
        ['Crop', selectedCrop.cropName],
        ['National average', selectedCrop.nationalAverage],
        [],
        ['County performance (Yield score 0-100)'],
        ['County', 'Yield score'],
        ...countyEntries.map(([c, v]) => [c, v]),
        [],
        ['International comparison (Yield score 0-100)'],
        ['Country', 'Yield score'],
        ...intlEntries.map(([c, v]) => [c, v]),
        [],
        ['Market trend (KES)'],
        ['Month', 'Price (KES)'],
        ...selectedCrop.marketTrend.map((m) => [m.month, m.price]),
        [],
        ['Growing conditions'],
        ...Object.entries(selectedCrop.growingConditions).map(([k, v]) => [k, v]),
        [],
        ['Best practices'],
        ...selectedCrop.bestPractices.map((p) => [p]),
      ];
      downloadCsv(`farmershub-crop-report-${selectedCrop.cropName}-${nowIso.slice(0, 10)}.csv`, rows);
    }
  };

  const exportReportPdf = async () => {
    const nowIso = new Date().toISOString();
    if (reportKind === 'county' && selectedCounty) {
      const htmlBody = `
        <h1>County detailed report</h1>
        <div class="muted">County: ${selectedCounty.name} • Region: ${selectedCounty.region}</div>
        <div class="muted">Source: ${selectedCounty.dataSource || 'Supabase'} • Updated: ${selectedCounty.lastUpdated ? new Date(selectedCounty.lastUpdated).toLocaleDateString() : '—'}</div>
        <div style="height:10px"></div>
        <div class="grid">
          <div class="card"><div class="k">Main crops</div><div class="v">${selectedCounty.mainCrops.length}</div></div>
          <div class="card"><div class="k">Market price rows</div><div class="v">${selectedCounty.marketPrices.length}</div></div>
          <div class="card"><div class="k">Resources</div><div class="v">${selectedCounty.resources.length}</div></div>
        </div>

        <h2>Climate</h2>
        <table>
          <tbody>
            <tr><th>Temperature</th><td>${selectedCounty.climate.temperature}</td></tr>
            <tr><th>Rainfall</th><td>${selectedCounty.climate.rainfall}</td></tr>
            <tr><th>Altitude</th><td>${selectedCounty.climate.altitude}</td></tr>
          </tbody>
        </table>

        <h2>Main crops</h2>
        <ul>${selectedCounty.mainCrops.map((c) => `<li>${c}</li>`).join('')}</ul>

        <h2>Market prices</h2>
        <table>
          <thead><tr><th>Commodity</th><th>Price (KES)</th><th>Unit</th><th>Date</th></tr></thead>
          <tbody>
            ${selectedCounty.marketPrices
              .map((p) => `<tr><td>${p.commodity}</td><td>${p.price}</td><td>${p.unit}</td><td>${new Date(p.date).toLocaleDateString()}</td></tr>`)
              .join('')}
          </tbody>
        </table>

        <h2>Government requirements</h2>
        <div class="row">
          <div class="col">
            <div class="card">
              <div class="k">Licenses</div>
              <ul>${selectedCounty.governmentRequirements.licenses.map((l) => `<li>${l}</li>`).join('')}</ul>
            </div>
          </div>
          <div class="col">
            <div class="card">
              <div class="k">Permits</div>
              <ul>${selectedCounty.governmentRequirements.permits.map((p) => `<li>${p}</li>`).join('')}</ul>
            </div>
          </div>
          <div class="col">
            <div class="card">
              <div class="k">Regulations</div>
              <ul>${selectedCounty.governmentRequirements.regulations.map((r) => `<li>${r}</li>`).join('')}</ul>
            </div>
          </div>
        </div>

        <h2>Farming tips</h2>
        ${selectedCounty.farmingTips
          .map((cat) => `<div class="card" style="margin-bottom:10px"><div class="k">${cat.category}</div><ul>${cat.tips.map((t) => `<li>${t}</li>`).join('')}</ul></div>`)
          .join('')}

        <h2>Resources</h2>
        <table>
          <thead><tr><th>Title</th><th>URL</th><th>Description</th></tr></thead>
          <tbody>
            ${selectedCounty.resources
              .map((r) => `<tr><td>${r.title}</td><td>${r.url}</td><td>${r.description}</td></tr>`)
              .join('')}
          </tbody>
        </table>
      `;
      await printHtml(`FarmersHub County report - ${selectedCounty.name}`, htmlBody);
      return;
    }

    if (reportKind === 'crop' && selectedCrop) {
      const countyEntries = Object.entries(selectedCrop.countyData).sort((a, b) => b[1] - a[1]);
      const intlEntries = Object.entries(selectedCrop.internationalComparison).sort((a, b) => b[1] - a[1]);
      const htmlBody = `
        <h1>Crop detailed report</h1>
        <div class="muted">Crop: ${selectedCrop.cropName} • National average: ${selectedCrop.nationalAverage}</div>
        <div class="muted">Source: ${selectedCrop.dataSource || 'Supabase'}</div>
        <div style="height:10px"></div>
        <div class="grid">
          <div class="card"><div class="k">Counties scored</div><div class="v">${countyEntries.length}</div></div>
          <div class="card"><div class="k">Countries compared</div><div class="v">${intlEntries.length}</div></div>
          <div class="card"><div class="k">Trend points</div><div class="v">${selectedCrop.marketTrend.length}</div></div>
        </div>

        <h2>County performance (Yield score 0-100)</h2>
        <table>
          <thead><tr><th>County</th><th>Yield score</th></tr></thead>
          <tbody>${countyEntries.map(([c, v]) => `<tr><td>${c}</td><td>${v}</td></tr>`).join('')}</tbody>
        </table>

        <h2>International comparison (Yield score 0-100)</h2>
        <table>
          <thead><tr><th>Country</th><th>Yield score</th></tr></thead>
          <tbody>${intlEntries.map(([c, v]) => `<tr><td>${c}</td><td>${v}</td></tr>`).join('')}</tbody>
        </table>

        <h2>Market trend (KES)</h2>
        <table>
          <thead><tr><th>Month</th><th>Price (KES)</th></tr></thead>
          <tbody>${selectedCrop.marketTrend.map((m) => `<tr><td>${m.month}</td><td>${m.price}</td></tr>`).join('')}</tbody>
        </table>

        <h2>Growing conditions</h2>
        <table>
          <thead><tr><th>Factor</th><th>Value</th></tr></thead>
          <tbody>${Object.entries(selectedCrop.growingConditions).map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('')}</tbody>
        </table>

        <h2>Best practices</h2>
        <ul>${selectedCrop.bestPractices.map((p) => `<li>${p}</li>`).join('')}</ul>
      `;
      await printHtml(`FarmersHub Crop report - ${selectedCrop.cropName}`, htmlBody);
    }
  };

  const generateMarketPriceChart = () => {
    if (!selectedCounty?.marketPrices || selectedCounty.marketPrices.length === 0) return null;

    const data = {
      labels: selectedCounty.marketPrices.map(price => price.commodity),
      datasets: [
        {
          label: 'Market Prices (KES)',
          data: selectedCounty.marketPrices.map(price => price.price),
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(199, 199, 199, 0.6)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(199, 199, 199, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: 'Market Prices by Commodity'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Price (KES)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateCropCountyComparisonChart = () => {
    if (!selectedCrop) return null;

    const countyNames = Object.keys(selectedCrop.countyData);
    
    const data = {
      labels: countyNames,
      datasets: [
        {
          label: 'Yield Score (0-100)',
          data: countyNames.map(county => selectedCrop.countyData[county]),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} Performance by County`
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Yield Score (0-100)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateInternationalComparisonChart = () => {
    if (!selectedCrop) return null;

    const countries = Object.keys(selectedCrop.internationalComparison);
    
    const data = {
      labels: countries,
      datasets: [
        {
          label: 'Yield Score (0-100)',
          data: countries.map(country => selectedCrop.internationalComparison[country]),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} International Comparison`
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Yield Score (0-100)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateMarketTrendChart = () => {
    if (!selectedCrop) return null;
    
    const data = {
      labels: selectedCrop.marketTrend.map(item => item.month),
      datasets: [
        {
          label: 'Price Trend (KES)',
          data: selectedCrop.marketTrend.map(item => item.price),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.2
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} Market Price Trend (Annual)`
        }
      },
      scales: {
        y: {
          beginAtZero: false,
          title: {
            display: true,
            text: 'Price (KES)'
          }
        }
      }
    };

    return <Line data={data} options={options} />;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <button
              onClick={() => setSearchMode('county')}
              className={`px-4 py-2 rounded ${
                searchMode === 'county'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Search by County
            </button>
            <button
              onClick={() => setSearchMode('crop')}
              className={`px-4 py-2 rounded ${
                searchMode === 'crop'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Search by Crop
            </button>
          </div>
          
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchMode === 'county' ? "Enter county name..." : "Enter crop name..."}
              className="flex-1 p-2 border rounded"
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading || !searchQuery.trim()}
              className="bg-gray-900 text-white px-5 py-2 rounded hover:bg-black disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={openDetailedReport}
              disabled={loading || (!selectedCounty && !selectedCrop)}
              className="border border-green-700 text-green-700 px-5 py-2 rounded hover:bg-green-50 disabled:opacity-50"
            >
              Detailed report
            </button>
          </div>
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </div>

      {selectedCounty && (
        <div className="space-y-8">
          <div className="text-xs text-gray-500 mb-4">
            {selectedCounty.dataSource || 'Data sourced from public agricultural databases and government resources.'} Updated {selectedCounty?.lastUpdated ? new Date(selectedCounty.lastUpdated).toLocaleDateString() : 'regularly'}.
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">{selectedCounty.name} County</h2>
            <p className="text-gray-600">Region: {selectedCounty.region}</p>
            
            <div className="mt-4">
              <h3 className="text-xl font-semibold mb-2">Climate</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Temperature</p>
                  <p>{selectedCounty.climate.temperature}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Rainfall</p>
                  <p>{selectedCounty.climate.rainfall}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Altitude</p>
                  <p>{selectedCounty.climate.altitude}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Main Crops</h3>
            <div className="flex flex-wrap gap-2">
              {selectedCounty.mainCrops.map((crop, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  {crop}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Market Prices</h3>
            <div className="mb-6 h-80">
              {generateMarketPriceChart()}
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2">Commodity</th>
                    <th className="px-4 py-2">Price (KES)</th>
                    <th className="px-4 py-2">Unit</th>
                    <th className="px-4 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCounty.marketPrices.map((price, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="px-4 py-2">{price.commodity}</td>
                      <td className="px-4 py-2">{price.price}</td>
                      <td className="px-4 py-2">{price.unit}</td>
                      <td className="px-4 py-2">{new Date(price.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Government Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium mb-2">Licenses</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.licenses.map((license, index) => (
                    <li key={index}>{license}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Permits</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.permits.map((permit, index) => (
                    <li key={index}>{permit}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Regulations</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.regulations.map((regulation, index) => (
                    <li key={index}>{regulation}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Farming Tips</h3>
            <div className="space-y-4">
              {selectedCounty.farmingTips.map((category, index) => (
                <div key={index}>
                  <h4 className="font-medium mb-2">{category.category}</h4>
                  <ul className="list-disc list-inside">
                    {category.tips.map((tip, tipIndex) => (
                      <li key={tipIndex}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Resources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCounty.resources.map((resource, index) => (
                <div key={index} className="border p-4 rounded">
                  <h4 className="font-medium mb-2">{resource.title}</h4>
                  <p className="text-gray-600 mb-2">{resource.description}</p>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Learn More
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedCrop && (
        <div className="space-y-8">
          <div className="text-xs text-gray-500 mb-4">
            {selectedCrop.dataSource}
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">{selectedCrop.cropName}</h2>
            
            <div className="mt-6 mb-8">
              <h3 className="text-xl font-semibold mb-4">County Performance Comparison</h3>
              <div className="h-96">
                {generateCropCountyComparisonChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Higher scores indicate better growing conditions and yield potential in that county
              </p>
            </div>
            
            <div className="mt-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">International Comparison</h3>
              <div className="h-80">
                {generateInternationalComparisonChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Comparing Kenya's {selectedCrop.cropName} production with other countries
              </p>
            </div>
            
            <div className="mt-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">Market Price Trend</h3>
              <div className="h-80">
                {generateMarketTrendChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Annual price fluctuations allow planning for optimal planting and selling time
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Growing Conditions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(selectedCrop.growingConditions).map(([condition, value], index) => (
                <div key={index} className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">{condition}</p>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Best Practices</h3>
            <div className="space-y-2">
              <ul className="list-disc list-inside">
                {selectedCrop.bestPractices.map((practice, index) => (
                  <li key={index} className="mb-2">{practice}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loginPromptOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-xl font-semibold">Login required</h3>
            <p className="mt-2 text-sm text-gray-600">
              Detailed reports are available to logged-in users so we can unlock the full platform capabilities.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setLoginPromptOpen(false)}
                className="rounded border px-4 py-2 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setLoginPromptOpen(false);
                  navigate('/login');
                }}
                className="rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCounty?.name && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="text-xl font-semibold">Official Market Prices (KAMIS)</h3>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <>
                  <input
                    id="kamis-upload-county-info"
                    type="file"
                    accept=".xls,.xlsx,.html,.htm"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files;
                      e.target.value = '';
                      if (!f || f.length === 0) return;
                      void handleUploadKamis(f);
                    }}
                  />
                  <button
                    onClick={() => {
                      const el = document.getElementById('kamis-upload-county-info') as HTMLInputElement | null;
                      el?.click();
                    }}
                    disabled={kamisIngesting}
                    className="rounded border px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {kamisIngesting ? 'Uploading…' : 'Upload KAMIS file'}
                  </button>
                </>
              )}
              <button
                onClick={() => selectedCounty?.name && loadKamis(selectedCounty.name)}
                disabled={kamisLoading || !selectedCounty?.name}
                className="rounded border px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                {kamisLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {kamisIngestMessage && <p className="mt-3 text-sm text-green-700">{kamisIngestMessage}</p>}
          {kamisError && <p className="mt-3 text-sm text-red-600">{kamisError}</p>}

          {!kamisError && kamisRows.length === 0 && !kamisLoading && (
            <p className="mt-3 text-sm text-gray-600">
              No official KAMIS rows found for this county yet. {isAdmin ? 'Upload the KAMIS export file, then refresh.' : 'Ask an admin to upload the KAMIS export file, then refresh.'}
            </p>
          )}

          {kamisRows.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Commodity</th>
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-4 py-2 text-right">Wholesale</th>
                    <th className="px-4 py-2 text-right">Retail</th>
                    <th className="px-4 py-2 text-right">Supply</th>
                    <th className="px-4 py-2 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {kamisRows.slice(0, 20).map((r, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="px-4 py-2">{r.commodity}</td>
                      <td className="px-4 py-2">{r.market}</td>
                      <td className="px-4 py-2 text-right">
                        {r.wholesale_price !== null && r.wholesale_price !== undefined ? `${r.wholesale_price}${r.unit ? `/${r.unit}` : ''}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.retail_price !== null && r.retail_price !== undefined ? `${r.retail_price}${r.unit ? `/${r.unit}` : ''}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">{r.supply_volume ?? '—'}</td>
                      <td className="px-4 py-2">{r.price_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 text-xs text-gray-500">
            Source: kamis.kilimo.go.ke (Ministry of Agriculture & Livestock Development)
          </div>
        </div>
      )}

      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b p-5">
              <div className="flex items-center gap-3">
                <img src="/icon.png" alt="FarmersHub" className="h-8 w-8 rounded" />
                <div>
                  <div className="text-lg font-semibold">FarmersHub • Detailed report</div>
                  <div className="text-xs text-gray-500">
                    {reportKind === 'county'
                      ? selectedCounty
                        ? `${selectedCounty.name} • ${selectedCounty.region}`
                        : ''
                      : selectedCrop
                        ? selectedCrop.cropName
                        : ''}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setReportOpen(false)}
                className="rounded border px-3 py-2 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-5">
              {reportKind === 'county' && selectedCounty && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">Main crops</div>
                      <div className="mt-1 text-2xl font-semibold">{selectedCounty.mainCrops.length}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">Market prices</div>
                      <div className="mt-1 text-2xl font-semibold">{selectedCounty.marketPrices.length}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">Resources</div>
                      <div className="mt-1 text-2xl font-semibold">{selectedCounty.resources.length}</div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="text-sm font-semibold">Climate</div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className="rounded bg-gray-50 p-3">
                        <div className="text-xs text-gray-500">Temperature</div>
                        <div className="mt-1 font-medium">{selectedCounty.climate.temperature}</div>
                      </div>
                      <div className="rounded bg-gray-50 p-3">
                        <div className="text-xs text-gray-500">Rainfall</div>
                        <div className="mt-1 font-medium">{selectedCounty.climate.rainfall}</div>
                      </div>
                      <div className="rounded bg-gray-50 p-3">
                        <div className="text-xs text-gray-500">Altitude</div>
                        <div className="mt-1 font-medium">{selectedCounty.climate.altitude}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="text-sm font-semibold">Market prices</div>
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left">
                            <th className="px-3 py-2">Commodity</th>
                            <th className="px-3 py-2">Price (KES)</th>
                            <th className="px-3 py-2">Unit</th>
                            <th className="px-3 py-2">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCounty.marketPrices.map((p, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2">{p.commodity}</td>
                              <td className="px-3 py-2">{p.price}</td>
                              <td className="px-3 py-2">{p.unit}</td>
                              <td className="px-3 py-2">{new Date(p.date).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {reportKind === 'crop' && selectedCrop && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">Counties scored</div>
                      <div className="mt-1 text-2xl font-semibold">{Object.keys(selectedCrop.countyData).length}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">Countries compared</div>
                      <div className="mt-1 text-2xl font-semibold">{Object.keys(selectedCrop.internationalComparison).length}</div>
                    </div>
                    <div className="rounded-lg border p-4">
                      <div className="text-xs text-gray-500">National average</div>
                      <div className="mt-1 text-2xl font-semibold">{selectedCrop.nationalAverage}</div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="text-sm font-semibold">Market trend</div>
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left">
                            <th className="px-3 py-2">Month</th>
                            <th className="px-3 py-2">Price (KES)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCrop.marketTrend.map((m, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2">{m.month}</td>
                              <td className="px-3 py-2">{m.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-3 border-t p-5">
              <button
                onClick={exportReportPdf}
                className="rounded bg-gray-900 px-4 py-2 text-white hover:bg-black"
              >
                Print / Save PDF
              </button>
              <button
                onClick={exportReportCsv}
                className="rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700"
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountyInfo; 
